import { useEffect, useState } from "react";
import client from "../lib/api";
import { Clock, Truck, CircleNotch, WarningCircle } from "@phosphor-icons/react";

/**
 * HoursOfServiceRoster.
 *
 * Dispatch-page card showing live HOS clock for every driver in the
 * connected Samsara fleet. Rows are sorted so expired/warning drivers
 * surface at the top — the ones dispatch actually needs to see.
 *
 * Auto-hides when the pilot isn't connected to Samsara (so the card
 * doesn't guilt-trip a fleet that doesn't have an ELD yet).
 */
const _TONE = {
  expired: "bg-red-500/15 text-red-300 border-red-500/30",
  warning: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  caution: "bg-amber-500/10 text-amber-200 border-amber-500/20",
  ok: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};

const _DUTY_TONE = {
  driving: "text-emerald-400",
  onDuty: "text-amber-400",
  offDuty: "text-neutral-500",
  sleeperBerth: "text-blue-400",
  yardMove: "text-purple-400",
  personalConveyance: "text-purple-400",
};

export default function HoursOfServiceRoster() {
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: st } = await client.get("/eld/status");
        if (!alive) return;
        setStatus(st);
        if (!st?.any_connected) {
          setLoading(false);
          return;
        }
        const { data: hos } = await client.get("/eld/hos");
        if (!alive) return;
        setData(hos);
        if (!hos?.ok) setError(hos?.error || "Fetch failed");
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "HOS fetch failed");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Silent hide when no ELD connected — either provider is optional
  if (loading) return null;
  if (!status?.any_connected) return null;
  if (error) {
    return (
      <div
        data-testid="hos-roster-error"
        className="card-surface p-4 flex items-start gap-2 text-xs text-red-300"
      >
        <WarningCircle size={14} className="mt-0.5 shrink-0" />
        <div>ELD HOS fetch failed: {error}</div>
      </div>
    );
  }

  const rows = data?.hos || [];
  if (rows.length === 0) return null;

  // Bucket for a quick top-strip summary
  const bySeverity = rows.reduce(
    (acc, r) => {
      acc[r.severity] = (acc[r.severity] || 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div data-testid="hos-roster" className="card-surface p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <Clock size={18} weight="fill" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
            ELD · Live HOS
          </div>
          <div className="font-display text-lg font-semibold text-white leading-tight">
            {rows.length} driver{rows.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide">
          {bySeverity.expired > 0 && (
            <span data-testid="hos-summary-expired" className="rounded-full px-2 py-0.5 bg-red-500/15 text-red-300 border border-red-500/30">
              {bySeverity.expired} out
            </span>
          )}
          {bySeverity.warning > 0 && (
            <span data-testid="hos-summary-warning" className="rounded-full px-2 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30">
              {bySeverity.warning} low
            </span>
          )}
          {bySeverity.ok > 0 && (
            <span className="rounded-full px-2 py-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              {bySeverity.ok} clear
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1.5" data-testid="hos-roster-list">
        {rows.slice(0, 12).map((r) => (
          <div
            key={r.samsara_id}
            data-testid={`hos-row-${r.samsara_id}`}
            className={`flex items-center gap-3 p-2.5 rounded-lg border ${_TONE[r.severity] || "border-white/5 bg-white/[0.02]"}`}
          >
            <Truck size={14} className={_DUTY_TONE[r.duty_status] || "text-neutral-500"} />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-white truncate">
                {r.name || "Unknown driver"}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                <span className={_DUTY_TONE[r.duty_status] || "text-neutral-500"}>
                  {r.duty_label}
                </span>
              </div>
            </div>
            <div className="shrink-0 grid grid-cols-3 gap-x-3 text-right">
              <div>
                <div data-testid={`hos-drive-${r.samsara_id}`} className="text-sm font-mono text-white tabular-nums">
                  {r.drive_remaining}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-600">
                  Drive
                </div>
              </div>
              <div className="hidden sm:block">
                <div className="text-xs font-mono text-neutral-400 tabular-nums">
                  {r.shift_remaining}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-600">
                  Shift
                </div>
              </div>
              <div className="hidden md:block">
                <div className="text-xs font-mono text-neutral-500 tabular-nums">
                  {r.cycle_remaining}
                </div>
                <div className="text-[9px] uppercase tracking-widest text-neutral-600">
                  Cycle
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {rows.length > 12 && (
        <div className="mt-3 text-[11px] text-neutral-500 text-center">
          Showing top 12 by urgency · {rows.length - 12} more drivers online
        </div>
      )}
    </div>
  );
}
